import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaImage, 
  FaUsers, 
  FaGlobe, 
  FaLock, 
  FaUserPlus,
  FaExclamationTriangle
} from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import { CustomButton2, CustomButton8 } from '../Templates';
import './styles/GroupsPage.css';
import './styles/CreateGroupPage.css';

const CreateGroupPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    visibility: 'public',
    location: '',
    schedule: '',
    meetingPoint: '',
    maxMembers: '',
    bannerImage: null
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Categories options
  const categories = [
    'commuters',
    'sports',
    'nature',
    'tourism',
    'hobby',
    'family',
    'other'
  ];

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        navigate('/login');
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        bannerImage: t('errors.file_too_large')
      }));
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        bannerImage: t('errors.invalid_file_type')
      }));
      return;
    }
    
    // Create image preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    setFormData(prev => ({
      ...prev,
      bannerImage: file
    }));
    
    // Clear error
    setErrors(prev => ({
      ...prev,
      bannerImage: null
    }));
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('errors.name_required');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('errors.description_required');
    }
    
    if (!formData.category) {
      newErrors.category = t('errors.category_required');
    }
    
    if (formData.maxMembers && (isNaN(formData.maxMembers) || parseInt(formData.maxMembers) < 0)) {
      newErrors.maxMembers = t('errors.invalid_member_count');
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      addToast({
        type: 'error',
        title: t('errors.form_has_errors'),
        description: t('errors.check_form_fields')
      });
      return;
    }
    
    setLoading(true);
    
    try {
      let bannerImageUrl = null;
      
      // Upload banner image if provided
      if (formData.bannerImage) {
        try {
          // Use events bucket as it's working for event uploads
          const bucketName = 'group';
          
          const fileExt = formData.bannerImage.name.split('.').pop();
          const fileName = `group_${user.id}_${Date.now()}.${fileExt}`;
          const filePath = `group_banners/${fileName}`;
          
          console.log('Attempting upload to:', { bucketName, filePath });
          
          // Upload the file with better error handling
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from(bucketName)
            .upload(filePath, formData.bannerImage, {
              // Add content type and cacheControl for better browser handling
              contentType: formData.bannerImage.type,
              cacheControl: '3600',
              // Make sure the file is publicly accessible
              upsert: true
            });
          
          if (uploadError) {
            console.error('Upload error:', uploadError);
            // Detailed error logging
            console.log('Upload details:', {
              bucket: bucketName,
              path: filePath,
              fileName,
              fileSize: formData.bannerImage.size,
              fileType: formData.bannerImage.type,
              error: uploadError
            });
            
            addToast({
              type: 'warning',
              title: t('errors.image_upload_failed'),
              description: uploadError.message || t('errors.continuing_without_image')
            });
          } else if (uploadData) {
            // Get public URL
            const { data: publicUrlData } = supabase.storage
              .from(bucketName)
              .getPublicUrl(filePath);
            
            console.log('Upload successful, URL:', publicUrlData);
            bannerImageUrl = publicUrlData.publicUrl;
          }
        } catch (uploadErr) {
          console.error('Image upload failed:', uploadErr);
          // Continue without image if upload fails
          addToast({
            type: 'warning',
            title: t('errors.image_upload_failed'),
            description: t('errors.continuing_without_image')
          });
        }
      }
      
      // Create group
      const { data: newGroup, error: groupError } = await supabase
        .from('groups')
        .insert({
          name: formData.name.trim(),
          description: formData.description.trim(),
          creator_id: user.id,
          visibility: formData.visibility,
          category: formData.category,
          location: formData.location.trim() || null,
          banner_image_url: bannerImageUrl,
          schedule: formData.schedule.trim() || null,
          meeting_point: formData.meetingPoint.trim() || null,
          max_members: formData.maxMembers ? parseInt(formData.maxMembers) : null,
          status: 'active'
        })
        .select()
        .single();
      
      if (groupError) throw groupError;
      
      addToast({
        type: 'success',
        title: t('groups.group_created'),
        description: t('groups.group_created_success')
      });
      
      // Navigate to the new group page
      navigate(`/groups/${newGroup.id}`);
      
    } catch (error) {
      console.error('Error creating group:', error);
      addToast({
        type: 'error',
        title: t('errors.creation_failed'),
        description: t('errors.group_creation_failed')
      });
      setLoading(false);
    }
  };
  
  // Go back to groups page
  const handleBack = () => {
    navigate('/groups');
  };

  return (
    <>
      <div className="groups-page">
        <div className="groups-header">
          <div className="back-button">
            <CustomButton8 onClick={handleBack}>
              <FaArrowLeft />
            </CustomButton8>
          </div>
          <h1 className="groups-title">{t('groups.create_group')}</h1>
        </div>
        
        <div className="create-group-container">
          <form className="create-group-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">{t('groups.group_name')} *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('groups.group_name_placeholder')}
              />
              {errors.name && (
                <div className="error-message">
                  <FaExclamationTriangle size={12} />
                  <span>{errors.name}</span>
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="description">{t('groups.group_description')} *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={t('groups.group_description_placeholder')}
              />
              {errors.description && (
                <div className="error-message">
                  <FaExclamationTriangle size={12} />
                  <span>{errors.description}</span>
                </div>
              )}
              <span className="hint">{t('groups.description_hint')}</span>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">{t('groups.category')} *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="">{t('groups.select_category')}</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {t(`groups.categories.${category}`)}
                    </option>
                  ))}
                </select>
                {errors.category && (
                  <div className="error-message">
                    <FaExclamationTriangle size={12} />
                    <span>{errors.category}</span>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="visibility">{t('groups.visibility')}</label>
                <select
                  id="visibility"
                  name="visibility"
                  value={formData.visibility}
                  onChange={handleChange}
                >
                  <option value="public">
                    {t('groups.public')} - <FaGlobe size={12} />
                  </option>
                  <option value="private">
                    {t('groups.private')} - <FaLock size={12} />
                  </option>
                  <option value="invite-only">
                    {t('groups.invite_only')} - <FaUserPlus size={12} />
                  </option>
                </select>
                <span className="hint">
                  {formData.visibility === 'public' && t('groups.public_hint')}
                  {formData.visibility === 'private' && t('groups.private_hint')}
                  {formData.visibility === 'invite-only' && t('groups.invite_only_hint')}
                </span>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="location">{t('groups.location')}</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder={t('groups.location_placeholder')}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="maxMembers">{t('groups.max_members')}</label>
                <input
                  type="number"
                  id="maxMembers"
                  name="maxMembers"
                  value={formData.maxMembers}
                  onChange={handleChange}
                  placeholder={t('groups.unlimited')}
                  min="0"
                />
                {errors.maxMembers && (
                  <div className="error-message">
                    <FaExclamationTriangle size={12} />
                    <span>{errors.maxMembers}</span>
                  </div>
                )}
                <span className="hint">{t('groups.max_members_hint')}</span>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="schedule">{t('groups.schedule')} ({t('optional')})</label>
                <input
                  type="text"
                  id="schedule"
                  name="schedule"
                  value={formData.schedule}
                  onChange={handleChange}
                  placeholder={t('groups.schedule_placeholder')}
                />
                <span className="hint">{t('groups.schedule_hint')}</span>
              </div>
              
              <div className="form-group">
                <label htmlFor="meetingPoint">{t('groups.meeting_point')} ({t('optional')})</label>
                <input
                  type="text"
                  id="meetingPoint"
                  name="meetingPoint"
                  value={formData.meetingPoint}
                  onChange={handleChange}
                  placeholder={t('groups.meeting_point_placeholder')}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('groups.banner_image')} ({t('optional')})</label>
              <div className={`image-preview ${imagePreview ? 'has-image' : ''}`}
                   style={imagePreview ? { backgroundImage: `url(${imagePreview})` } : {}}
              >
                {!imagePreview && (
                  <div className="upload-placeholder">
                    <FaImage size={30} />
                    <p>{t('groups.upload_banner')}</p>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="bannerImage"
                accept="image/jpeg, image/png"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
              
              <div className="image-actions">
                <CustomButton2
                  label={imagePreview ? t('groups.change_image') : t('groups.select_image')}
                  onClick={(e) => {
                    e.preventDefault(); // Prevent form submission
                    document.getElementById('bannerImage').click();
                  }}
                  type="button"
                  icon={<FaImage />}
                />
                <span className="hint">{t('groups.image_requirements')}</span>
              </div>
              
              {errors.bannerImage && (
                <div className="error-message">
                  <FaExclamationTriangle size={12} />
                  <span>{errors.bannerImage}</span>
                </div>
              )}
            </div>
            
            <div className="form-actions">
              <CustomButton2
                label={t('buttons.cancel')}
                onClick={handleBack}
                type="button"
              />
              <CustomButton2
                label={loading ? t('app.creating') : t('buttons.create_group')}
                type="submit"
                disabled={loading}
                icon={loading ? <Spinner size="small" showText={false} /> : <FaUsers />}
              />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateGroupPage;
